import React, { useState } from "react";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Section from "../../../layout/Section";
import { DialogSection } from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import Autocomplete from "../../../common/inputs/Autocomplete";
import { AdminGesellschaft } from "../../../../services/api/admin";
import { useAdminGesellschaften } from "../hooks/useAdminGesellschaften";
import { useToast } from "../../../../contexts/ToastContext";
import { colors } from "../../../../styles/colors";

interface GesellschaftSectionProps {
  orgId: string;
}

interface EditState {
  name: string;
  rechtsform: string;
}

interface GesellschaftRowProps {
  gesellschaft: AdminGesellschaft;
  rechtsformOptions: string[];
  onUpdate: (
    id: string,
    name: string,
    rechtsform: string | null,
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const GesellschaftRow: React.FC<GesellschaftRowProps> = ({
  gesellschaft,
  rechtsformOptions,
  onUpdate,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditState>({
    name: gesellschaft.name,
    rechtsform: gesellschaft.rechtsform ?? "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onUpdate(
        gesellschaft.id,
        form.name.trim(),
        form.rechtsform.trim() || null,
      );
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name: gesellschaft.name,
      rechtsform: gesellschaft.rechtsform ?? "",
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
        <TextField
          size="small"
          label="Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          sx={{ flex: 2 }}
        />
        <Autocomplete
          label="Rechtsform"
          options={rechtsformOptions}
          value={form.rechtsform}
          onChange={(v) => setForm((f) => ({ ...f, rechtsform: v }))}
          sx={{ flex: 1 }}
        />
        <Tooltip title="Speichern">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Abbrechen">
          <IconButton size="small" onClick={handleCancel} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.75,
        px: 1,
        borderRadius: 1,
        "&:hover": { backgroundColor: colors.background.tableHeader },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
        <Typography variant="body2">{gesellschaft.name}</Typography>
        {gesellschaft.rechtsform && (
          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
            {gesellschaft.rechtsform}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          opacity: 0,
          "&:hover": { opacity: 1 },
          ".MuiBox-root:hover &": { opacity: 1 },
        }}
      >
        <Tooltip title="Bearbeiten">
          <IconButton size="small" onClick={() => setEditing(true)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Löschen">
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(gesellschaft.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

interface NewRowProps {
  typ: "haupt" | "verwaltung";
  rechtsformOptions: string[];
  onCreate: (name: string, rechtsform: string | null) => Promise<void>;
  onCancel: () => void;
}

const NewRow: React.FC<NewRowProps> = ({
  typ,
  rechtsformOptions,
  onCreate,
  onCancel,
}) => {
  const [name, setName] = useState("");
  const [rechtsform, setRechtsform] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onCreate(name.trim(), rechtsform.trim() || null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
      <TextField
        size="small"
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        sx={{ flex: 2 }}
        placeholder={
          typ === "haupt"
            ? "z.B. Windpark Nordsee GmbH"
            : "z.B. Verwaltungsgesellschaft mbH"
        }
      />
      <Autocomplete
        label="Rechtsform"
        options={rechtsformOptions}
        value={rechtsform}
        onChange={setRechtsform}
        sx={{ flex: 1 }}
      />
      <Tooltip title="Speichern">
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={handleSave}
            disabled={saving || !name.trim()}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Abbrechen">
        <IconButton size="small" onClick={onCancel} disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const GesellschaftSection: React.FC<GesellschaftSectionProps> = ({ orgId }) => {
  const { gesellschaften, rechtsformOptions, loading, create, update, remove } =
    useAdminGesellschaften(orgId);
  const { showSuccess, showError } = useToast();
  const [addingTyp, setAddingTyp] = useState<"haupt" | "verwaltung" | null>(
    null,
  );

  const haupt = gesellschaften.find((g) => g.typ === "haupt") ?? null;
  const verwaltung = gesellschaften.filter((g) => g.typ === "verwaltung");

  const handleCreate = async (
    typ: "haupt" | "verwaltung",
    name: string,
    rechtsform: string | null,
  ) => {
    try {
      await create({ name, rechtsform, typ });
      showSuccess(
        `${typ === "haupt" ? "Hauptgesellschaft" : "Verwaltungsgesellschaft"} wurde angelegt`,
      );
      setAddingTyp(null);
    } catch {
      showError("Gesellschaft konnte nicht angelegt werden");
    }
  };

  const handleUpdate = async (
    id: string,
    name: string,
    rechtsform: string | null,
  ) => {
    try {
      await update(id, { name, rechtsform });
      showSuccess("Änderungen gespeichert");
    } catch {
      showError("Gesellschaft konnte nicht aktualisiert werden");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await remove(id);
      showSuccess("Gesellschaft wurde gelöscht");
    } catch {
      showError("Gesellschaft konnte nicht gelöscht werden");
    }
  };

  return (
    // Ohne Titel: Die Seite heißt schon „Gesellschaft" (SeitenTitel aus PageTemplate).
    <Section>
      <DialogSection title="Hauptgesellschaft" first>
        {haupt ? (
          <GesellschaftRow
            gesellschaft={haupt}
            rechtsformOptions={rechtsformOptions}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ) : addingTyp === "haupt" ? (
          <NewRow
            typ="haupt"
            rechtsformOptions={rechtsformOptions}
            onCreate={(name, rechtsform) =>
              handleCreate("haupt", name, rechtsform)
            }
            onCancel={() => setAddingTyp(null)}
          />
        ) : (
          <Button variant="primary" onClick={() => setAddingTyp("haupt")}>
            <AddIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
            Hauptgesellschaft anlegen
          </Button>
        )}
      </DialogSection>

      <DialogSection title="Verwaltungsgesellschaften">
        {verwaltung.map((g) => (
          <GesellschaftRow
            key={g.id}
            gesellschaft={g}
            rechtsformOptions={rechtsformOptions}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ))}
        {addingTyp === "verwaltung" ? (
          <NewRow
            typ="verwaltung"
            rechtsformOptions={rechtsformOptions}
            onCreate={(name, rechtsform) =>
              handleCreate("verwaltung", name, rechtsform)
            }
            onCancel={() => setAddingTyp(null)}
          />
        ) : (
          <Box sx={{ mt: verwaltung.length > 0 ? 1 : 0 }}>
            <Button
              variant="primary"
              onClick={() => setAddingTyp("verwaltung")}
            >
              <AddIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
              Verwaltungsgesellschaft anlegen
            </Button>
          </Box>
        )}
      </DialogSection>
    </Section>
  );
};

export default GesellschaftSection;
